import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmt } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft, Download, TrendingDown, TrendingUp, Users } from "lucide-react";
import { useHouseholdMode } from "@/hooks/useHouseholdMode";
import { useAuth } from "@/context/AuthContext";

const monthLabel = (m) => {
  if (!m) return "";
  const [y, mo] = m.split("-");
  const d = new Date(parseInt(y), parseInt(mo) - 1, 1);
  return d.toLocaleString("en-US", { month: "long", year: "numeric" });
};

export default function MonthlyReport() {
  const { user } = useAuth();
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const { enabled: hhMode, param } = useHouseholdMode();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/monthly_report/available_months", { params: param });
        setMonths(data);
        setMonth(data[0] || null);
      } catch {
        toast.error("Could not load months");
      }
    })();
  }, [hhMode]); // eslint-disable-line

  useEffect(() => {
    if (!month) return;
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/monthly_report", { params: { month, ...param } });
        setReport(data);
      } catch {
        toast.error("Could not load report");
      }
      setLoading(false);
    })();
  }, [month, hhMode]); // eslint-disable-line

  const netDelta = useMemo(() => {
    if (!report?.cash_flow || !report?.prev_cash_flow) return null;
    const prev = report.prev_cash_flow.net || 0;
    const cur = report.cash_flow.net || 0;
    return cur - prev;
  }, [report]);

  const downloadPdf = async () => {
    if (!month) return;
    setDownloading(true);
    try {
      const res = await api.get("/monthly_report.pdf", { params: { month, ...param }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `moneywise-report-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Report downloaded");
    } catch {
      toast.error("Could not download PDF");
    }
    setDownloading(false);
  };

  return (
    <div className="px-6 pt-10 pb-6">
      <div className="flex items-center justify-between">
        <Link to="/app" className="flex items-center gap-1 text-slate-500 hover:text-slate-900 text-sm" data-testid="report-back-btn">
          <ChevronLeft size={16} /> Home
        </Link>
        {hhMode && (
          <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">
            <Users size={11} /> Household view
          </div>
        )}
      </div>

      <div className="mt-4">
        <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Monthly report</div>
        <h1 className="font-manrope text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900" data-testid="report-title">
          {monthLabel(month) || "Loading…"}
        </h1>
        <div className="text-sm text-slate-500 mt-1">{user?.name}</div>
      </div>

      {/* Month picker */}
      {months.length > 0 && (
        <div className="mt-5 -mx-6 px-6 overflow-x-auto no-scrollbar">
          <div className="flex gap-2">
            {months.map((m) => {
              const active = m === month;
              return (
                <button
                  key={m}
                  data-testid={`report-month-${m}`}
                  onClick={() => setMonth(m)}
                  className={`shrink-0 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                >
                  {monthLabel(m)}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {loading || !report ? (
        <div className="mt-8 text-slate-400">Preparing your report…</div>
      ) : (
        <>
          {/* Summary hero */}
          <div className="mt-6 rounded-3xl bg-slate-900 text-white p-6 relative overflow-hidden">
            <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-emerald-500/15 blur-3xl" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-widest text-emerald-300 font-semibold">Summary</div>
              <p className="mt-2 text-slate-100 leading-relaxed text-[15px]" data-testid="report-summary">{report.summary}</p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <HeroKpi label="Net cash flow" value={report.cash_flow.net} tone={report.cash_flow.net >= 0 ? "pos" : "neg"} testId="report-kpi-net" />
                <HeroKpi label="Potential savings" value={report.potential_savings} tone="pos" testId="report-kpi-potential" />
              </div>
              {netDelta !== null && report.prev_cash_flow?.net !== 0 && (
                <div className="mt-3 text-xs text-slate-300 flex items-center gap-1.5">
                  {netDelta >= 0 ? <TrendingUp size={12} className="text-emerald-400" /> : <TrendingDown size={12} className="text-rose-400" />}
                  <span className={netDelta >= 0 ? "text-emerald-400" : "text-rose-400"}>{netDelta >= 0 ? "+" : "-"}{fmt(Math.abs(netDelta))}</span>
                  <span>vs {monthLabel(report.prev_month)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Cash flow row */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <MiniStat label="Income" value={report.cash_flow.income} tone="pos" testId="report-income" />
            <MiniStat label="Expenses" value={report.cash_flow.expenses} testId="report-expenses" />
            <MiniStat label="Prev net" value={report.prev_cash_flow?.net || 0} testId="report-prev-net" />
          </div>

          {/* Top categories */}
          <div className="mt-6">
            <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">Top categories</div>
            <div className="mt-3 space-y-2">
              {report.top_categories.map((c) => {
                const delta = (c.current_month || 0) - (c.avg_6mo || 0);
                const up = delta > 0;
                return (
                  <div key={c.category} className="flex items-center justify-between p-3 rounded-xl border border-slate-200">
                    <div>
                      <div className="font-medium text-slate-900 text-sm">{c.category}</div>
                      <div className="text-xs text-slate-400">6-mo avg {fmt(c.avg_6mo)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-manrope font-bold text-slate-900 tabular-nums">{fmt(c.current_month)}</div>
                      {c.avg_6mo > 0 && (
                        <div className={`text-xs flex items-center gap-1 justify-end ${up ? "text-rose-500" : "text-emerald-600"}`}>
                          {up ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                          {up ? "+" : "−"}{fmt(Math.abs(delta))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Biggest changes */}
          {report.biggest_changes?.length > 0 && (
            <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Biggest changes vs your 6-mo average</div>
              <div className="mt-3 space-y-2">
                {report.biggest_changes.map((c) => {
                  const delta = (c.current_month || 0) - (c.avg_6mo || 0);
                  return (
                    <div key={c.category} className="flex items-center justify-between">
                      <span className="text-slate-900 font-medium text-sm">{c.category}</span>
                      <span className={`text-sm font-semibold tabular-nums ${delta > 0 ? "text-rose-600" : "text-emerald-600"}`}>
                        {delta > 0 ? "+" : "−"}{fmt(Math.abs(delta))}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Priorities */}
          {report.priorities?.length > 0 && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-widest text-slate-400 font-semibold">3 priorities for next month</div>
              <div className="mt-3 space-y-2">
                {report.priorities.map((p, i) => (
                  <div key={i} className="flex gap-3 p-4 rounded-2xl bg-emerald-50 border border-emerald-100" data-testid={`report-priority-${i}`}>
                    <div className="w-7 h-7 rounded-full bg-emerald-500 text-slate-900 font-manrope font-bold text-sm flex items-center justify-center shrink-0">{i + 1}</div>
                    <div className="text-sm text-slate-800 leading-relaxed">{p}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Download */}
          <button
            data-testid="report-download-pdf"
            onClick={downloadPdf}
            disabled={downloading}
            className="mt-8 w-full flex items-center justify-center gap-2 rounded-full py-4 font-semibold bg-slate-900 hover:bg-slate-800 text-white transition-colors disabled:opacity-60"
          >
            <Download size={16} /> {downloading ? "Preparing PDF…" : "Download PDF"}
          </button>
          <p className="mt-3 text-[11px] text-slate-400 text-center">Educational insights, not financial advice.</p>
        </>
      )}
    </div>
  );
}

function HeroKpi({ label, value, tone, testId }) {
  const color = tone === "pos" ? "text-emerald-400" : "text-white";
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div data-testid={testId} className={`mt-1 font-manrope text-2xl font-extrabold tracking-tight tabular-nums ${color}`}>{fmt(value)}</div>
    </div>
  );
}

function MiniStat({ label, value, tone, testId }) {
  const color = tone === "pos" ? "text-emerald-600" : "text-slate-900";
  return (
    <div className="rounded-2xl border border-slate-200 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{label}</div>
      <div data-testid={testId} className={`mt-1 font-manrope text-lg font-extrabold tracking-tight tabular-nums ${color}`}>{fmt(value)}</div>
    </div>
  );
}
